import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { GetProductOptions } from "../utils/product";

const COLOR_MAP = {
  "שחור": "#000000",
  "לבן": "#ffffff",
  "אפור": "#9e9e9e",
  "כחול": "#1e88e5",
  "אדום": "#e53935",
  "ורוד": "#ff69b4",
  "ירוק": "#43a047",
  "חום": "#6d4c41",
  "צהוב": "#fdd835",
  "כתום": "#fb8c00",
  "סגול": "#8e24aa",
  "תכלת": "#4fc3f7",
  // תוספות
  "ורוד פוקסיה": "#ff1493",
  "טורקיז": "#246d66",
  "ירוק כהה": "#1b5e20",
  "סגול בהיר": "#ce93d8",
  "ורוד בהיר": "#f8bbd0",
  "כסף": "#c0c0c0",
  "זהב": "#e6c558",
  "נחושת": "#b87333",
  "ארד": "#cd7f32",
};

function getField(o, name) {
  return o?.[name] ?? o?.[name.charAt(0).toLowerCase() + name.slice(1)];
}

function normalizeOption(o) {
  return {
    id: getField(o, "Id"),
    optionType: getField(o, "OptionType"),
    value: String(getField(o, "Value") ?? "").trim(),
    extraPrice: getField(o, "ExtraPrice"),
    quantity: getField(o, "Quantity"),
  };
}

function Circle({ children, selected, onClick, title, bg, textColor = "#111" }) {
  return (
    <Tooltip title={title || ""} placement="top">
      <Box
        onClick={onClick}
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          cursor: "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bg,
          border: selected ? "2px solid #000" : "1px solid #cfcfcf",
          boxShadow: selected ? 2 : 0,
          fontSize: 11,
          fontWeight: 700,
          color: textColor,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

/**
 * props:
 * - productId: number
 * - onChange?: (selection: { Color?: option, Size?: option }) => void
 * - initialSelection?: { Color?: optionId, Size?: optionId }
 */
export default function ProductVariationPicker({ productId, onChange, initialSelection }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState({}); // { Color: option, Size: option }

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const raw = await GetProductOptions(productId);
        const normalized = (raw || []).map(normalizeOption).filter(o => o.optionType && o.value);

        if (cancelled) return;

        setOptions(normalized);

        const nextSelected = {};
        const byType = normalized.reduce((acc, o) => {
          (acc[o.optionType] ||= []).push(o);
          return acc;
        }, {});

        ["Color", "Size"].forEach((t) => {
          const list = byType[t] || [];
          if (!list.length) return;

          const initialId = initialSelection?.[t];
          const found = initialId ? list.find(x => String(x.id) === String(initialId)) : null;
          nextSelected[t] = found || list[0];
        });

        setSelected(nextSelected);
        onChange?.(nextSelected);
      } catch (e) {
        console.error("Failed to load product options:", e);
        if (!cancelled) setOptions([]);
      }
    };

    if (productId) load();

    return () => { cancelled = true; };
  }, [productId]);

  const grouped = useMemo(() => {
    const byType = { Color: [], Size: [] };
    for (const o of options) {
      if (o.optionType === "Color") byType.Color.push(o);
      else if (o.optionType === "Size") byType.Size.push(o);
    }
    return byType;
  }, [options]);

  const handlePick = (type, opt) => {
    const next = { ...selected, [type]: opt };
    setSelected(next);
    onChange?.(next);
  };

  if (!grouped.Color.length && !grouped.Size.length) return null;

  return (
    <Box sx={{ mt: 0 }}>
      {/* צבעים - נשאר עיגולים */}
      {grouped.Color.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            צבע
          </Typography>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
            {grouped.Color.map((opt) => {
              const cssColor = COLOR_MAP[opt.value] || opt.value;
              const isSelected = selected.Color?.id === opt.id;

              return (
                <Circle
                  key={opt.id ?? `color-${opt.value}`}
                  title={opt.value}
                  selected={isSelected}
                  onClick={() => handlePick("Color", opt)}
                  bg={cssColor}
                  textColor="transparent"
                >
                  .
                </Circle>
              );
            })}
          </Box>
        </Box>
      )}

      {/* מידות - Dropdown (חלון נפתח) */}
      {grouped.Size.length > 0 && (
        <Box sx={{ mt: grouped.Color.length ? 1 : 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {/* מידה */}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id={`size-label-${productId}`}>לבחירה</InputLabel>
            <Select
              labelId={`size-label-${productId}`}
              value={selected.Size?.id ?? ""}
              label=" לבחירה"
              onChange={(e) => {
                const id = e.target.value;
                const opt = grouped.Size.find(x => String(x.id) === String(id));
                if (opt) handlePick("Size", opt);
              }}
            >
              {grouped.Size.map((opt) => (
                <MenuItem key={opt.id ?? `size-${opt.value}`} value={opt.id}>
                  {opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
